import { useState, useCallback, useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { useRunStore } from "../state/runStore";
import { useAuthStore } from "../state/authStore";
import { api } from "../utils/api";
import { fuelUnitForDisplay } from "../utils/formatters";
import { useWeekCars, CarSwitcherBar } from "../components/CarSwitcher";
import type { FuelPlanResult, RaceData } from "../types";

const LITERS_PER_GALLON = 3.785411784;

export default function FuelPlanner() {
  const { trackWeekId: routeTrackWeekId } = useParams<{
    teamId?: string;
    trackWeekId?: string;
  }>();
  const { selectedTrackWeekId: storeTrackWeekId } = useRunStore();
  const { user } = useAuthStore();

  const selectedTrackWeekId = routeTrackWeekId || storeTrackWeekId;

  const units = user?.preferences?.units || "standard";
  const fuelUnit = fuelUnitForDisplay(units);
  const isMetric = units === "metric";

  // Per-car scoping (#279): only active on multi-car custom weeks; otherwise
  // selectedCarId is undefined and the fetch is unscoped (legacy behavior).
  const { cars, selectedCarId, setCar } = useWeekCars(selectedTrackWeekId);

  const [fuelPerLap, setFuelPerLap] = useState<string>("");
  const [fuelCapacity, setFuelCapacity] = useState<string>("");
  const [raceLaps, setRaceLaps] = useState<string>("");

  const [raceData, setRaceData] = useState<RaceData | null>(null);
  const [raceDataLoading, setRaceDataLoading] = useState(false);
  const [raceDataError, setRaceDataError] = useState<string | null>(null);

  const [result, setResult] = useState<FuelPlanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const autoCalcDone = useRef(false);

  useEffect(() => {
    if (!selectedTrackWeekId) return;

    let cancelled = false;
    (async () => {
      setRaceDataLoading(true);
      setRaceDataError(null);
      autoCalcDone.current = false;
      // Drop any prior plan: a stale result would otherwise stay on screen
      // (and, after a units change, be re-labeled in the wrong unit).
      setResult(null);
      setError(null);
      try {
        const data = await api<RaceData>(
          `/api/strategy/race-data?track_week_id=${selectedTrackWeekId}` +
            (selectedCarId ? `&car_id=${encodeURIComponent(selectedCarId)}` : "")
        );
        if (cancelled) return;
        setRaceData(data);

        if (data.raceLaps != null) {
          setRaceLaps(String(data.raceLaps));
        }

        if (data.fuelCapacity != null) {
          const cap = isMetric
            ? (data.fuelCapacityLiters ?? data.fuelCapacity * LITERS_PER_GALLON)
            : data.fuelCapacity;
          setFuelCapacity(String(Math.round(cap * 10) / 10));
        }

        if (data.avgFuelPerLap != null && data.avgFuelPerLap > 0) {
          const fpl = isMetric
            ? data.avgFuelPerLap
            : data.avgFuelPerLap / LITERS_PER_GALLON;
          setFuelPerLap(fpl.toFixed(3));
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const e = err as { message?: string };
          setRaceDataError(e.message || "Failed to load race data");
        }
      } finally {
        if (!cancelled) setRaceDataLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTrackWeekId, isMetric, selectedCarId]);

  useEffect(() => {
    if (autoCalcDone.current) return;
    if (raceDataLoading || !raceData) return;
    const fpl = parseFloat(fuelPerLap);
    const cap = parseFloat(fuelCapacity);
    const laps = parseInt(raceLaps, 10);
    if (!isNaN(fpl) && fpl > 0 && !isNaN(cap) && cap > 0 && !isNaN(laps) && laps > 0) {
      autoCalcDone.current = true;
      doCalculate(fpl, cap, laps);
    }
  }, [fuelPerLap, fuelCapacity, raceLaps, raceData, raceDataLoading]);

  const doCalculate = useCallback(
    async (fpl: number, cap: number, laps: number) => {
      setLoading(true);
      setError(null);
      try {
        const selected = raceData?.selectedStrategy;
        const pitStopLaps: number[] | undefined =
          selected === "race_derived" && raceData?.raceDerivedStrategy?.pitWindows?.length
            ? raceData.raceDerivedStrategy.pitWindows.map((pw) => pw.optimal)
            : undefined;

        const plan = await api<FuelPlanResult>("/api/strategy/fuel", {
          method: "POST",
          body: JSON.stringify({
            fuelPerLap: fpl,
            fuelCapacity: cap,
            raceLaps: laps,
            pitStopLaps: pitStopLaps?.length ? pitStopLaps : undefined,
          }),
        });
        setResult(plan);
      } catch (err: unknown) {
        const e = err as { message?: string };
        // Clear the old plan so a failed recompute doesn't leave a stale
        // strategy on screen looking current.
        setResult(null);
        setError(e.message || "Calculation failed");
      } finally {
        setLoading(false);
      }
    },
    [raceData]
  );

  const compute = useCallback(async () => {
    const fpl = parseFloat(fuelPerLap);
    const cap = parseFloat(fuelCapacity);
    const laps = parseInt(raceLaps, 10);

    if (isNaN(fpl) || fpl <= 0) { setError("Enter a valid fuel per lap value"); return; }
    if (isNaN(cap) || cap <= 0) { setError("Enter a valid tank capacity"); return; }
    if (isNaN(laps) || laps <= 0) { setError("Enter a valid race length"); return; }

    await doCalculate(fpl, cap, laps);
  }, [fuelPerLap, fuelCapacity, raceLaps, doCalculate]);

  const confidenceBadgeClass =
    raceData?.confidence === "high" ? "badge-green"
      : raceData?.confidence === "medium" ? "badge-yellow"
      : raceData?.confidence === "low" ? "badge-orange"
      : "";

  const totalRaceLaps = parseInt(raceLaps, 10) || 0;

  return (
    <div className="page page--wide fade-in">
      {/* Tab content sits under the WeekWorkspace hero — no page title here, just controls */}
      <div style={st.toolbar}>
        {raceData && (
          <span className={`badge ${confidenceBadgeClass}`} style={st.confidenceBadge}>
            <span className={`flag-dot ${raceData.confidence === "high" ? "flag-dot--green" : "flag-dot--yellow"}`} />
            {raceData.confidence} confidence
          </span>
        )}
        <button className="btn btn-primary" onClick={compute} disabled={loading}>
          {loading ? "Calculating…" : "Calculate strategy"}
        </button>
      </div>

      <CarSwitcherBar cars={cars} selectedCarId={selectedCarId} setCar={setCar} />

      {/* Grid */}
      <div style={st.grid}>
        {/* ── Parameters Panel ── */}
        <div className="card" style={st.paramPanel}>
          <div className="section-label" style={st.panelTitle}>Race parameters</div>

          {/* Status messages */}
          {raceDataLoading && (
            <div style={st.statusMsg}>
              <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
              Loading race data...
            </div>
          )}
          {raceDataError && <div style={{ ...st.statusMsg, color: "var(--flag-red)" }}>{raceDataError}</div>}
          {raceData && !raceDataError && !raceDataLoading && (
            <div style={{ ...st.statusMsg, color: "var(--live)" }}>
              Race data loaded
              {raceData.raceLaps ? ` · ${raceData.raceLaps} laps` : ""}
              {raceData.fuelCapacity
                ? ` · ${isMetric
                    ? `${(raceData.fuelCapacityLiters ?? raceData.fuelCapacity * LITERS_PER_GALLON).toFixed(1)} L`
                    : `${raceData.fuelCapacity} gal`
                  } tank`
                : ""}
              {raceData.fuelCapacity && raceData.fuelCapacitySource === "table"
                ? " (default, verify)"
                : ""}
              {raceData.avgFuelPerLap
                ? ` · ${(isMetric
                    ? raceData.avgFuelPerLap
                    : raceData.avgFuelPerLap / LITERS_PER_GALLON
                  ).toFixed(3)} ${fuelUnit}/lap avg`
                : ""}
              {raceData.avgFuelPerLap && raceData.avgFuelPerLapRuns > 0
                ? ` (${raceData.avgFuelPerLapRuns} run${raceData.avgFuelPerLapRuns === 1 ? "" : "s"})`
                : ""}
            </div>
          )}
          {!raceData && !raceDataLoading && !raceDataError && !selectedTrackWeekId && (
            <div style={{ ...st.statusMsg, color: "var(--text-muted)" }}>
              Select a track week to auto-fill race data.
            </div>
          )}

          <div style={st.inputStack}>
            <InputField
              label="Race Length"
              unit="Laps"
              type="number"
              step="1"
              placeholder="e.g. 85"
              value={raceLaps}
              onChange={setRaceLaps}
            />
            <InputField
              label="Tank Capacity"
              unit={fuelUnit}
              type="number"
              step="0.1"
              placeholder={isMetric ? "e.g. 66.2" : "e.g. 17.5"}
              value={fuelCapacity}
              onChange={setFuelCapacity}
              hint={
                raceData?.fuelCapacitySource === "telemetry"
                  ? "Measured from your telemetry"
                  : raceData?.fuelCapacitySource === "table"
                    ? "Series default — verify against your car"
                    : undefined
              }
            />
            <InputField
              label="Fuel Per Lap"
              unit={fuelUnit}
              type="number"
              step="0.001"
              placeholder={isMetric ? "e.g. 1.450" : "e.g. 0.383"}
              value={fuelPerLap}
              onChange={setFuelPerLap}
              hint={
                raceData?.avgFuelPerLap
                  ? `Team average from ${raceData.avgFuelPerLapRuns || 0} uploaded run${raceData.avgFuelPerLapRuns === 1 ? "" : "s"}`
                  : undefined
              }
            />
          </div>

          {error && <div style={st.errorMsg}>{error}</div>}

          {/* Pit window source info */}
          {raceData && (
            <div style={st.pitSourceBox}>
              <div style={st.pitSourceHeader}>
                <span style={st.pitSourceLabel}>
                  {raceData.selectedStrategy === "race_derived" ? "Race-Derived Pit Window" : "Estimated Pit Strategy"}
                </span>
              </div>

              {raceData.selectedStrategy === "race_derived" && raceData.raceDerivedStrategy?.pitWindows?.length ? (
                <>
                  {raceData.raceDerivedStrategy.pitWindows.map((pw) => (
                    <div key={pw.stop} style={{ marginBottom: "0.4rem" }}>
                      {(raceData.raceDerivedStrategy?.avgGreenStops ?? 1) > 1 && (
                        <div style={st.pitStopLabel}>Stop {pw.stop}</div>
                      )}
                      <div style={st.pitLaps}>
                        Laps {pw.earliest}–{pw.latest}
                        <span style={st.pitOptimal}>optimal lap {pw.optimal}</span>
                      </div>
                    </div>
                  ))}
                  {raceData.raceDerivedStrategy.dominantStrategy && (
                    <span className="badge badge-blue">{raceData.raceDerivedStrategy.dominantStrategy} strategy</span>
                  )}
                  {raceData.raceDerivedStrategy.sanityWarning && (
                    <div style={st.pitWarning}>{raceData.raceDerivedStrategy.sanityWarning}</div>
                  )}
                  <div style={st.pitSourceMeta}>
                    <span>
                      From {raceData.raceDerivedStrategy.racesAnalyzed ?? raceData.raceDerivedStrategy.greenRacesUsed} green-flag race
                      {(raceData.raceDerivedStrategy.racesAnalyzed ?? raceData.raceDerivedStrategy.greenRacesUsed) === 1 ? "" : "s"}
                      {raceData.raceDerivedStrategy.qualifyingDrivers != null && raceData.raceDerivedStrategy.minIrating != null
                        ? ` · ${raceData.raceDerivedStrategy.qualifyingDrivers} driver${raceData.raceDerivedStrategy.qualifyingDrivers === 1 ? "" : "s"} over ${raceData.raceDerivedStrategy.minIrating} iR`
                        : ""}
                    </span>
                    {raceData.raceDerivedStrategy.confidence && raceData.raceDerivedStrategy.confidence !== "high" && (
                      <span className={`badge ${raceData.raceDerivedStrategy.confidence === "low" ? "badge-orange" : "badge-yellow"}`}>
                        {raceData.raceDerivedStrategy.confidence === "low" ? "thin data" : "limited data"}
                      </span>
                    )}
                  </div>
                  {raceData.raceDerivedStrategy.relaxations?.map((note, i) => (
                    <div key={i} style={st.pitRelaxation}>{note}</div>
                  ))}
                </>
              ) : raceData.estimatedStrategy && raceData.estimatedStrategy.stopsRequired != null ? (
                <>
                  <div style={st.pitLaps}>
                    {raceData.estimatedStrategy.stopsRequired}-stop
                    {raceData.estimatedStrategy.stopLaps.length > 0 && (
                      <span style={st.pitOptimal}>
                        {" "}at lap{raceData.estimatedStrategy.stopLaps.length > 1 ? "s" : ""}{" "}
                        {raceData.estimatedStrategy.stopLaps.join(", ")}
                      </span>
                    )}
                  </div>
                  {raceData.estimatedStrategy.lapsPerTank != null && (
                    <div style={{ fontSize: "0.8125rem", color: "var(--text-secondary)", marginTop: "0.25rem" }}>
                      {raceData.estimatedStrategy.lapsPerTank} laps per tank · {raceData.estimatedStrategy.marginLaps} laps margin
                    </div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>
                  Upload a baseline test run to enable fuel strategy estimation.
                </div>
              )}

              <div style={st.pitMeta}>{raceData.confidenceReason}</div>
            </div>
          )}
        </div>

        {/* ── Strategy Results Panel ── */}
        <div style={st.rightCol}>
          {!result && !loading && (
            <div className="empty-state">
              {raceDataLoading && <span className="spinner" />}
              <div className="empty-state-title">
                {raceDataLoading ? "Loading race data" : "Ready to calculate"}
              </div>
              <div className="empty-state-text">
                {raceDataLoading
                  ? "Pulling race data from recent sessions."
                  : "Enter your race parameters and hit Calculate to see your optimal fuel strategy."}
              </div>
            </div>
          )}

          {result && (
            <>
              {/* Strategy headline + stat cards */}
              <div className="card" style={st.strategyCard}>
                <div style={st.strategyHeader}>
                  <div>
                    <div style={st.strategyKicker}>Optimal race strategy</div>
                    <div style={st.strategyHeadline}>
                      {result.stopsRequired === 0
                        ? "No-stop strategy"
                        : `${result.stopsRequired}-stop strategy`}
                    </div>
                  </div>
                  {result.source && (
                    <span className={`badge ${result.source === "race_data" ? "badge-green" : "badge-blue"}`}>
                      {result.source === "race_data" ? "stops from race data · fuel estimated" : "estimated"}
                    </span>
                  )}
                </div>

                {/* Infeasibility / warning banner — a plan that can't physically
                    be completed must shout, not hide behind a green margin. */}
                {result.feasible === false && (
                  <div style={st.infeasibleBanner}>
                    ⚠ {result.warning || "This plan exceeds your tank range on at least one stint — you would run out of fuel."}
                  </div>
                )}

                <div style={st.statGrid}>
                  <StatCard
                    label="Laps / Tank"
                    value={String(result.lapsPerTank)}
                  />
                  <StatCard
                    label="Pit Stops"
                    value={String(result.stopsRequired)}
                    color="var(--live)"
                  />
                  <StatCard
                    label="Safety Margin"
                    value={`${result.marginLaps > 0 ? "+" : ""}${result.marginLaps}`}
                    suffix="laps"
                    color={
                      result.marginLaps <= 0
                        ? "var(--flag-red)"
                        : result.marginLaps < 2
                          ? "var(--flag-yellow)"
                          : "var(--flag-green)"
                    }
                  />
                  <StatCard
                    label="Total Fuel"
                    value={String(result.totalFuelNeeded)}
                    suffix={fuelUnit}
                    color="var(--live)"
                  />
                </div>
              </div>

              {/* Pit Windows Timeline */}
              {result.stopsRequired > 0 && (
                <div className="card" style={{ padding: "1.25rem" }}>
                  <div style={st.sectionHeader}>
                    <h3 style={st.sectionTitle}>Pit Windows</h3>
                    <span className="chip">
                      Total Race: {totalRaceLaps} Laps
                    </span>
                  </div>
                  <RaceTimeline
                    raceLaps={totalRaceLaps}
                    stopLaps={result.optimalStopLaps}
                    fuelToAdd={result.fuelToAdd}
                    fuelUnit={fuelUnit}
                  />
                </div>
              )}

              {result.stopsRequired === 0 && (
                <div className="card" style={{ ...st.noPitCard, padding: "1.25rem" }}>
                  <div style={{ color: "var(--flag-green)", fontFamily: "var(--font-display)", fontSize: "1rem", fontWeight: 700 }}>
                    Full distance, no pit required
                  </div>
                  <div style={{ color: "var(--text-secondary)", fontSize: "0.88rem", marginTop: "0.35rem" }}>
                    You can run the full race distance without pitting for fuel.
                  </div>
                </div>
              )}

              {/* Fuel Matrix Table */}
              {result.stopsRequired > 0 && (
                <div className="card" style={{ padding: 0, overflow: "hidden" }}>
                  <div style={st.tableHeader}>
                    <h3 style={st.sectionTitle}>Fuel Matrix</h3>
                  </div>
                  <FuelMatrix
                    result={result}
                    raceLaps={totalRaceLaps}
                    fuelCapacity={parseFloat(fuelCapacity) || 0}
                    fuelPerLap={parseFloat(fuelPerLap) || 0}
                    fuelUnit={fuelUnit}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Sub-components ---------- */

function InputField({
  label,
  unit,
  hint,
  onChange,
  ...inputProps
}: {
  label: string;
  unit: string;
  hint?: string;
  onChange: (v: string) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) {
  return (
    <div>
      <label style={st.fieldLabel}>{label}</label>
      <div style={st.inputWrap}>
        <input
          {...inputProps}
          className="input input-data"
          style={st.input}
          onChange={(e) => onChange(e.target.value)}
        />
        <span style={st.inputUnit}>{unit}</span>
      </div>
      {hint && <div style={st.fieldHint}>{hint}</div>}
    </div>
  );
}

function StatCard({
  label,
  value,
  suffix,
  color,
}: {
  label: string;
  value: string;
  suffix?: string;
  color?: string;
}) {
  return (
    <div style={st.statCard}>
      <div style={st.statLabel}>{label}</div>
      <div className="mono" style={{ ...st.statValue, color: color || "var(--text-strong)" }}>
        {value}
        {suffix && <span style={st.statSuffix}>{suffix}</span>}
      </div>
    </div>
  );
}

function RaceTimeline({
  raceLaps,
  stopLaps,
  fuelToAdd,
  fuelUnit,
}: {
  raceLaps: number;
  stopLaps: number[];
  fuelToAdd: number[];
  fuelUnit: string;
}) {
  if (raceLaps <= 0) return null;

  const points = [0, ...stopLaps, raceLaps];
  const segments: Array<{ start: number; end: number; stint: number }> = [];
  for (let i = 0; i < points.length - 1; i++) {
    segments.push({ start: points[i], end: points[i + 1], stint: i + 1 });
  }

  const isLast = (i: number) => i === segments.length - 1;

  return (
    <div>
      {/* Track bar */}
      <div style={st.trackBar}>
        {segments.map((seg, i) => {
          const width = ((seg.end - seg.start) / raceLaps) * 100;
          return (
            <div
              key={i}
              style={{
                width: `${width}%`,
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                background: i === 0 || isLast(i)
                  ? "rgba(91, 141, 239, 0.12)"
                  : "rgba(255, 255, 255, 0.03)",
                borderRight: isLast(i) ? "none" : "1px solid rgba(91, 141, 239, 0.2)",
                transition: "background 160ms ease",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = i === 0 || isLast(i) ? "rgba(91, 141, 239, 0.2)" : "rgba(255, 255, 255, 0.06)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = i === 0 || isLast(i) ? "rgba(91, 141, 239, 0.12)" : "rgba(255, 255, 255, 0.03)"; }}
            >
              <span className="mono" style={st.stintLabel}>
                {isLast(i) ? "Sprint" : `Stint ${seg.stint}`} ({seg.end - seg.start}L)
              </span>
              {/* Pit marker */}
              {!isLast(i) && (
                <div style={st.pitMarker} title={`Pit ${seg.stint}: Lap ${seg.end}`} />
              )}
            </div>
          );
        })}
      </div>

      {/* Lap labels */}
      <div style={st.lapLabels}>
        <span className="mono" style={st.lapLabel}>Lap 1</span>
        {stopLaps.map((lap, i) => (
          <span
            key={i}
            className="mono"
            style={{
              ...st.lapLabel,
              position: "absolute",
              left: `${(lap / raceLaps) * 100}%`,
              transform: "translateX(-50%)",
            }}
          >
            Lap {lap} <span style={{ color: "var(--flag-green)" }}>(Pit {i + 1})</span>
          </span>
        ))}
        <span className="mono" style={{ ...st.lapLabel, position: "absolute", right: 0 }}>
          Lap {raceLaps}
        </span>
      </div>

      {/* Stop schedule */}
      <div style={st.stopList}>
        {stopLaps.map((lap, i) => (
          <div key={i} style={st.stopRow}>
            <span style={st.stopNum}>Stop {i + 1}</span>
            <span style={st.stopDetail}>
              Lap <span className="mono" style={st.monoAccent}>{lap}</span>
              {fuelToAdd[i] != null && (
                <>
                  {" — Add "}
                  <span className="mono" style={st.monoAccent}>{fuelToAdd[i].toFixed(2)}</span>
                  {` ${fuelUnit}`}
                </>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FuelMatrix({
  result,
  raceLaps,
  fuelCapacity,
  fuelPerLap,
  fuelUnit,
}: {
  result: FuelPlanResult;
  raceLaps: number;
  fuelCapacity: number;
  fuelPerLap: number;
  fuelUnit: string;
}) {
  const points = [0, ...result.optimalStopLaps, raceLaps];
  const stints: Array<{
    stint: number;
    laps: number;
    startFuel: number;
    endFuel: number;
    pitAdd: number | null;
  }> = [];

  let prevEnd = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const laps = points[i + 1] - points[i];
    // A pit add happens at the START of every stint after the first; that splash
    // is fuelToAdd[i-1] (the first stint starts on a full tank, no add).
    const pitAdd = i === 0
      ? null
      : (i - 1 < result.fuelToAdd.length ? result.fuelToAdd[i - 1] : null);
    // Carry leftover fuel across the stop — a splash-and-go does NOT refill to
    // full, so later stints don't start at tank capacity.
    const startFuel = i === 0
      ? fuelCapacity
      : Math.min(prevEnd + (pitAdd ?? 0), fuelCapacity);
    const endFuel = Math.max(0, startFuel - laps * fuelPerLap);
    prevEnd = endFuel;
    stints.push({ stint: i + 1, laps, startFuel, endFuel, pitAdd });
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table className="table striped">
        <thead>
          <tr>
            <th>Stint</th>
            <th>Target Laps</th>
            <th>Start Fuel ({fuelUnit})</th>
            <th>End Fuel ({fuelUnit})</th>
            <th>Pit Add ({fuelUnit})</th>
          </tr>
        </thead>
        <tbody>
          {stints.map((s) => (
            <tr key={s.stint}>
              <td style={{ fontWeight: 600 }}>{s.stint}</td>
              <td className="mono" style={{ color: "var(--live)" }}>{s.laps}</td>
              <td className="mono">{s.startFuel.toFixed(2)}</td>
              <td className="mono" style={{ color: "var(--text-secondary)" }}>{s.endFuel.toFixed(2)}</td>
              <td className="mono" style={{ color: s.pitAdd != null ? "var(--flag-green)" : "var(--text-muted)" }}>
                {s.pitAdd != null ? `+${s.pitAdd.toFixed(2)}` : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Styles ---------- */

const st: Record<string, CSSProperties> = {
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "0.75rem",
    flexWrap: "wrap",
    marginBottom: "1.25rem",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    flexShrink: 0,
  },
  confidenceBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.4rem",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "minmax(320px, 5fr) 7fr",
    gap: "1.5rem",
    alignItems: "start",
  },

  // Parameters panel
  paramPanel: {
    position: "relative",
    padding: "1.5rem",
  },
  panelTitle: {
    display: "block",
    marginBottom: "1rem",
  },
  statusMsg: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    fontSize: "0.8125rem",
    marginBottom: "0.75rem",
    opacity: 0.85,
  },

  inputStack: {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  },
  fieldLabel: {
    display: "block",
    fontFamily: "var(--font-body)",
    fontSize: "0.85rem",
    fontWeight: 500,
    color: "var(--text-secondary)",
    marginBottom: "0.3rem",
  },
  inputWrap: {
    position: "relative",
  },
  input: {
    minHeight: "2.75rem",
    padding: "0.6rem 0.85rem",
    paddingRight: "3.5rem",
    borderRadius: "var(--r-sm)",
  },
  inputUnit: {
    position: "absolute",
    right: 14,
    top: "50%",
    transform: "translateY(-50%)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.8125rem",
    color: "var(--text-muted)",
    pointerEvents: "none",
  },
  fieldHint: {
    fontSize: "0.8125rem",
    color: "var(--text-muted)",
    marginTop: "0.2rem",
  },
  errorMsg: {
    marginTop: "0.75rem",
    fontSize: "0.85rem",
    color: "var(--flag-red)",
  },

  // Pit source box
  pitSourceBox: {
    marginTop: "1rem",
    padding: "0.85rem",
    borderRadius: "var(--r-sm)",
    background: "var(--live-soft)",
    border: "1px solid rgba(91, 141, 239, 0.12)",
  },
  pitSourceHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: "0.5rem",
  },
  pitSourceLabel: {
    fontFamily: "var(--font-body)",
    fontSize: "0.82rem",
    fontWeight: 600,
    color: "var(--live)",
  },
  pitStopLabel: {
    fontSize: "0.8125rem",
    color: "var(--text-muted)",
    marginBottom: "0.1rem",
  },
  pitLaps: {
    fontSize: "1.15rem",
    fontWeight: 700,
    color: "var(--text-strong)",
    fontVariantNumeric: "tabular-nums",
  },
  pitOptimal: {
    fontSize: "0.82rem",
    fontWeight: 500,
    color: "var(--text-secondary)",
    marginLeft: 6,
  },
  pitWarning: {
    fontSize: "0.8125rem",
    color: "var(--flag-yellow)",
    marginTop: "0.5rem",
    fontStyle: "italic",
  },
  infeasibleBanner: {
    background: "var(--flag-red-soft)",
    border: "1px solid rgba(248, 113, 113, 0.32)",
    borderRadius: "var(--r-sm)",
    color: "var(--flag-red)",
    fontSize: "0.82rem",
    fontWeight: 600,
    padding: "0.6rem 0.8rem",
    margin: "0.75rem 0 0.25rem",
    lineHeight: 1.4,
  },
  pitMeta: {
    fontSize: "0.8125rem",
    color: "var(--text-muted)",
    marginTop: "0.5rem",
    lineHeight: 1.5,
  },
  pitSourceMeta: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0.5rem",
    fontSize: "0.8125rem",
    color: "var(--text-secondary)",
    marginTop: "0.55rem",
  },
  pitRelaxation: {
    fontSize: "0.8125rem",
    color: "var(--text-muted)",
    marginTop: "0.3rem",
    lineHeight: 1.45,
  },

  // Right column
  rightCol: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },

  // Strategy card
  strategyCard: {
    padding: "1.5rem",
  },
  strategyHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "1.25rem",
  },
  strategyKicker: {
    fontFamily: "var(--font-body)",
    fontSize: "0.85rem",
    fontWeight: 500,
    color: "var(--text-secondary)",
    marginBottom: "0.25rem",
  },
  strategyHeadline: {
    fontFamily: "var(--font-display)",
    fontSize: "clamp(1.8rem, 3vw, 2.4rem)",
    fontWeight: 800,
    color: "var(--text-strong)",
    lineHeight: 1.05,
    letterSpacing: "-0.04em",
  },

  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
    gap: "0.75rem",
  },
  statCard: {
    padding: "0.85rem",
    borderRadius: "var(--r-sm)",
    background: "rgba(255, 255, 255, 0.025)",
    border: "1px solid var(--rule)",
  },
  statLabel: {
    fontFamily: "var(--font-body)",
    fontSize: "0.82rem",
    fontWeight: 500,
    color: "var(--text-secondary)",
    marginBottom: "0.35rem",
  },
  statValue: {
    fontSize: "1.45rem",
    fontWeight: 700,
    lineHeight: 1.1,
  },
  statSuffix: {
    fontSize: "0.8125rem",
    fontWeight: 400,
    opacity: 0.6,
    marginLeft: "0.25rem",
  },

  // Section headers (inside cards)
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1rem",
  },
  sectionTitle: {
    fontFamily: "var(--font-display)",
    fontSize: "1.05rem",
    fontWeight: 700,
    color: "var(--text-strong)",
    letterSpacing: "-0.02em",
  },

  // Timeline
  trackBar: {
    display: "flex",
    height: 48,
    borderRadius: "var(--r-sm)",
    overflow: "hidden",
    border: "1px solid var(--rule)",
    background: "var(--ink-input)",
  },
  stintLabel: {
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: "var(--text-secondary)",
    letterSpacing: "-0.01em",
  },
  pitMarker: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: 3,
    background: "var(--flag-green)",
    transform: "translateX(50%)",
    zIndex: 10,
  },

  lapLabels: {
    position: "relative",
    height: 24,
    marginTop: 4,
  },
  lapLabel: {
    fontSize: "0.8125rem",
    color: "var(--text-muted)",
  },

  // Stop list
  stopList: {
    display: "flex",
    flexDirection: "column",
    gap: "0.35rem",
    marginTop: "1rem",
    paddingTop: "1rem",
    borderTop: "1px solid var(--rule-faint)",
  },
  stopRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "0.45rem 0.65rem",
    borderRadius: "var(--r-sm)",
    background: "rgba(255, 255, 255, 0.02)",
    borderLeft: "3px solid var(--live)",
  },
  stopNum: {
    fontFamily: "var(--font-mono)",
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: "var(--live)",
    letterSpacing: "0.02em",
    minWidth: 52,
  },
  stopDetail: {
    fontSize: "0.88rem",
    color: "var(--text-default)",
  },
  monoAccent: {
    fontWeight: 700,
    color: "var(--live)",
  },

  // No pit card
  noPitCard: {
    borderLeft: "4px solid var(--flag-green)",
  },

  // Table header
  tableHeader: {
    padding: "0.9rem 1rem",
    borderBottom: "1px solid var(--rule)",
    background: "var(--ink-1)",
  },
};
